import type { RentalApplicationAddress } from '../models/rental-application';
import { startCase, toLower } from 'lodash-es';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { formatDate, parseDate } from '@zg-rentals/formatters';
import { formatAddress } from '@zg-rentals/visual-cohesion';
// Reexport to preserve unit tests and backwards compatibility
// with previous consumers, RA-8443 was created to remove this export and move unit tests
export { parseDate, formatDate };
/**
 * Basic US phone pre-validation just to be sure
 * that the number is in the expected format.
 * More strict validation is done by the libphonenumber-js library, but the library
 * is not enough since it considers other formats as valid.
 *
 * Ex. libphonenumber-js considers "11-234-567-8901" as valid
 */
const PHONE_REGEX = /^(1|\+1)?\s*[-.]?\s*\(?\d{3}\)?\s*[-.]?\s*\d{3}\s*[-.]?\s*\d{4}$/;

export function isValidUSPhoneNumber(phoneNumber: string) {
  return parseUSPhoneNumber(phoneNumber) !== null;
}

/**
 * Return the different parts of a US phone number or null if it is not a valid US phone number
 */
function parseUSPhoneNumber(phoneNumber: string) {
  if (!PHONE_REGEX.test(phoneNumber)) {
    return null;
  }

  const phone = parsePhoneNumberFromString(phoneNumber, 'US');
  if (!phone) return null;

  // isPossible() check is also needed because otherwise 7-digit local-only numbers are considered valid
  if (!phone.isPossible() || !phone.isValid()) {
    return null;
  }

  return phone;
}

/**
 * Format a phone number from a valid US phone format, including E.164 format such as +11234567890
 * to (123) 456-7890. Returns the original string if the phone is not a valid US phone number
 */
export function getPhoneNumberScreenFormat(phone?: string | null) {
  if (!phone) {
    return '';
  }
  const parsedPhoneNumber = parseUSPhoneNumber(phone);
  return parsedPhoneNumber ? parsedPhoneNumber.formatNational() : phone;
}

// Format a zip from server format 234647307 to 23464-7307
export function getZipCodeScreenFormat(zip?: string | null): string {
  if (!zip) {
    return '';
  }

  if (zip.length === 5) {
    return zip;
  }

  if (zip.length === 10 && zip[5] === '-') {
    return zip;
  }

  const formattedZip = `${zip.slice(0, 5)}-${zip.slice(5)}`;
  return formattedZip;
}

// Format a zip to server format 23464-7307 to 234647307
export function getZipCodeServerFormat(zipCodeDisplayed: string) {
  if (!zipCodeDisplayed) {
    return '';
  }
  return zipCodeDisplayed.replace('-', '');
}

/**
 * Format an US phone number from (123) 456 7890 to E.164 format, (e.g. +1234567890)
 * Returns the original string if the phone is not a valid US phone number
 */
export function getPhoneNumberServerFormat(phoneNumberDisplayed: string) {
  const parsedPhoneNumber = parseUSPhoneNumber(phoneNumberDisplayed);
  if (!parsedPhoneNumber) {
    return phoneNumberDisplayed;
  }

  return parsedPhoneNumber.format('E.164');
}

export function getFormattedAddress(address?: RentalApplicationAddress | null) {
  if (!address) {
    return '';
  }
  return formatAddress({ ...address, zip: getZipCodeScreenFormat(address.zip) });
}

export function pluralize(string: string, unit: number) {
  return `${unit} ${string}${unit > 1 ? 's' : ''}`;
}

export function getTitleCase(string: string) {
  return startCase(toLower(string));
}

/** Gets the minimum date from an array of dates */
export function dateMin(dates: Array<Date | null | undefined>) {
  return dates.filter((date) => !!date).reduce((acc, date) => (date < acc ? date : acc));
}

/** Gets the maximum date from an array of dates */
export function dateMax(dates: Array<Date | null | undefined>) {
  return dates.filter((date) => !!date).reduce((acc, date) => (date > acc ? date : acc));
}

/**
 * Returns a serverDate formatted for display. If serverDate is not in the correct
 * format returns the original value as-is.
 * @param serverDate
 * @returns
 */
export function getFormattedDateValue(serverDate: string) {
  try {
    if (serverDate && isServerDate(serverDate)) {
      const formattedValue = formatDate(serverDate, 'long');
      return formattedValue;
    }
  } catch {
    // ignore
  }
  return serverDate;
}

/**
 * Returns true if the date is a valid "serverDate" formatted as yyyy-MM-dd
 * @param value
 * @returns
 */
export function isServerDate(value: string) {
  return /^(19[0-9][0-9]|2[0-1][0-9]{2})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(value);
}

export const SERVER_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Parses a date in the display format generated by getFormattedDateValue()
 * to the server format yyyy-MM-dd.
 *
 * If the displayDate can't be converted back and forth and remain the same,
 * it will be returned as-is as an invalid server-date, unless @param forceConversion is true.
 *
 * Do not set forceConversion to true in onChange callbacks or the input field will change
 * while the user is typing, causing manual input issues
 * @param displayDate
 * @returns
 */
export function parseDisplayDateToServerDate(displayDate: string, forceConversion = false) {
  try {
    const parsedDate = parseDate(displayDate);
    if (parsedDate) {
      const serverDate = formatDate(parsedDate, 'server');

      const reformattedValue = getFormattedDateValue(serverDate);

      if (forceConversion || displayDate === reformattedValue) {
        return serverDate;
      }
    }
  } catch {
    // ignore
  }
  return displayDate;
}

export function getTodaysDateInDisplayFormat() {
  return formatDate(new Date(), 'long');
}

export function formatTimeToDisplayDate(dateStr: string) {
  try {
    const result = formatDate(dateStr, 'long');
    return result || null;
  } catch {
    return null;
  }
}

export function getFullName({
  firstName,
  middleName,
  lastName,
}: {
  firstName?: string;
  middleName?: string;
  lastName?: string;
}) {
  if (!firstName && !lastName && !middleName) {
    return 'name unavailable';
  }
  return `${firstName} ${middleName ? `${middleName} ` : ''}${lastName}`;
}

export function getPossesiveName(name: string) {
  return `${name}${name.endsWith('s') ? '’' : '’s'}`;
}

/** Returns today's date at local time 0:00 in ISO format, the Date value is
 * stable across the whole day but the returned instance is not stable (it always
 * returns a new Date instance)
 */
export const today = (): Date => {
  const ret = new Date();
  ret.setHours(0, 0, 0, 0);
  return ret;
};

/**
 * Returns 50 years ago at local time 0:00 in ISO format, the Date value is
 * stable across the whole day but the returned instance is not stable (it always
 * returns a new Date instance)
 * @returns
 */
export const date50YearsAgo = (): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setFullYear(date.getFullYear() - 50);

  return date;
};

export const getNumberFromString = (string?: string | null) => {
  if (!string) {
    return 0;
  }
  if (typeof string === 'string') {
    string = string.replace(/[^0-9.]/g, '');
  }

  return !isNaN(parseFloat(string)) ? parseFloat(string) : 0;
};
